import React, { useState, useRef, useEffect } from 'react'; // Added useState, useRef, useEffect
import { useTranslation } from 'react-i18next';
import { Seller, Contact, BankDetails } from '../../types/invoiceTypes';
import { useAuth } from '../../context/AuthContext'; // Added useAuth
import { storage } from '../../firebaseConfig'; // Added storage import
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage"; // Added storage functions
import logger from '../../utils/logger';
import UnitCodeSelector from '../ui/UnitCodeSelector';

interface SellerDetailsFormProps {
  seller: Seller;
  setSeller: (seller: Seller) => void;
  onLogoRemoved: () => Promise<void>;
  compact?: boolean; // Optional compact mode for AccountPage
}

const SellerDetailsForm: React.FC<SellerDetailsFormProps> = ({ seller, setSeller, onLogoRemoved, compact = false }) => {
  const { t } = useTranslation('seller');
  const { currentUser } = useAuth(); // Get current user for storage path
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null); // Ref for file input
  const [previewInvoiceNumber, setPreviewInvoiceNumber] = useState<string>('');
  
  // Hinzugefügt: State für Validierungsfehler
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  // Hinzugefügt: State für Vorschau der Angebotsnummer
  const [previewOfferNumber, setPreviewOfferNumber] = useState<string>('');
  
  // Debug: Only log important seller data changes in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && seller.logoUrl) {
      logger.debug('Logo loaded:', { hasLogo: !!seller.logoUrl });
    }
  }, [seller.logoUrl]);

  // Generiere Rechnungsnummer-Vorschau bei Änderung
  useEffect(() => {
    if (seller.invoiceNumberFormat) {
      const currentNumber = seller.invoiceNumberCurrent ?? seller.invoiceNumberStart ?? 1;
      const preview = generateInvoiceNumber(seller.invoiceNumberFormat, currentNumber, new Date());
      setPreviewInvoiceNumber(preview);
    } else {
      setPreviewInvoiceNumber('');
    }
  }, [seller.invoiceNumberFormat, seller.invoiceNumberCurrent, seller.invoiceNumberStart]);

  // NEU: Generiere Angebotsnummer-Vorschau bei Änderung
  useEffect(() => {
    if (seller.offerNumberFormat) {
      // Stelle sicher, dass die Typen Zahlen sind
      const current = parseInt(String(seller.offerNumberCurrent ?? 0), 10);
      const start = parseInt(String(seller.offerNumberStart ?? 1), 10);
      const numberToUse = Math.max(current, start); // Priorisiere Startnummer wenn höher
      
      const preview = generateOfferNumber(seller.offerNumberFormat, numberToUse, new Date());
      setPreviewOfferNumber(preview);
    } else {
      setPreviewOfferNumber('');
    }
  }, [seller.offerNumberFormat, seller.offerNumberCurrent, seller.offerNumberStart]);

  // Funktion zum Generieren einer Beispiel-Rechnungsnummer
  const generateInvoiceNumber = (format: string, currentNumber: number, date: Date): string => {
    // Ersetze Platzhalter für Jahr und Monat
    let result = format
      .replace(/yyyy/g, date.getFullYear().toString())
      .replace(/yy/g, date.getFullYear().toString().substr(2))
      .replace(/mm/g, (date.getMonth() + 1).toString().padStart(2, '0'))
      .replace(/MM/g, (date.getMonth() + 1).toString().padStart(2, '0'));
    
    // Ersetze die Nummer-Platzhalter mit der laufenden Nummer
    // Format: {number} oder {number:3} für Formatierung mit führenden Nullen
    const numberMatch = result.match(/\{number(?::(\d+))?\}/);
    if (numberMatch) {
      const padding = numberMatch[1] ? parseInt(numberMatch[1]) : 3; // Standard: 3 Stellen
      const formattedNumber = currentNumber.toString().padStart(padding, '0');
      result = result.replace(numberMatch[0], formattedNumber);
    } else {
      // Wenn kein spezifischer Platzhalter gefunden wurde, füge die Nummer am Ende hinzu
      result += currentNumber.toString().padStart(3, '0');
    }
    
    return result;
  };

  // Funktion zum Generieren einer Beispiel-Angebotsnummer (identisch zu Rechnung, anderer Name)
  const generateOfferNumber = (format: string, currentNumber: number, date: Date): string => {
    // Ersetze Platzhalter für Jahr und Monat
    let result = format
      .replace(/yyyy/g, date.getFullYear().toString())
      .replace(/yy/g, date.getFullYear().toString().substr(2))
      .replace(/mm/g, (date.getMonth() + 1).toString().padStart(2, '0'))
      .replace(/MM/g, (date.getMonth() + 1).toString().padStart(2, '0'));
    
    // Ersetze die Nummer-Platzhalter mit der laufenden Nummer
    const numberMatch = result.match(/\{number(?::(\d+))?\}/);
    if (numberMatch) {
      const padding = numberMatch[1] ? parseInt(numberMatch[1]) : 4; // Standard: 4 Stellen für Angebote
      const formattedNumber = currentNumber.toString().padStart(padding, '0');
      result = result.replace(numberMatch[0], formattedNumber);
    } else {
      // WIEDER HINZUGEFÜGT: Wenn kein spezifischer Platzhalter gefunden wurde, füge die Nummer am Ende hinzu
      // Verwende Standard-Padding 3 wie bei Rechnungen für Konsistenz
      result += currentNumber.toString().padStart(3, '0'); 
      // Only log in development and only once per session
      if (process.env.NODE_ENV === 'development' && !sessionStorage.getItem('offerNumberFormatWarningShown')) {
        logger.warn("Offer number format missing {number} placeholder. Using default padding.");
        sessionStorage.setItem('offerNumberFormatWarningShown', 'true');
      }
    }
    
    return result;
  };

  // Handler for simple top-level fields mit Validierung
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let finalValue = name === 'country' ? value.toUpperCase() : value;
    
    // Validierung für Pflichtfelder - aber erlaube Eingaben!
    const newValidationErrors = { ...validationErrors };
    
    // Überprüfen, ob das Feld ein Pflichtfeld ist
    const isRequiredField = ['name', 'street', 'zip', 'city', 'country'].includes(name);
    
    // Validierungsfehler nur setzen, nicht die Eingabe blockieren
    if (isRequiredField && (!finalValue || finalValue.trim() === '')) {
      newValidationErrors[name] = 'Dieses Feld darf nicht leer sein.';
    } else {
      delete newValidationErrors[name];
    }
    
    // Für Pflichtfelder: Verwende empty string statt null, um konsistent mit der Validierung zu bleiben
    if (isRequiredField && (!finalValue || finalValue.trim() === '')) {
      finalValue = '';
    }
    
    setValidationErrors(newValidationErrors);
    setSeller({ ...seller, [name]: finalValue });
  };

  // Handler for nested fields (Contact, BankDetails)
  const handleNestedChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldGroup: 'contact' | 'bankDetails'
  ) => {
    const { name, value } = e.target;
    const field = name as keyof (Contact | BankDetails);

    const newSeller = { ...seller };

    if (fieldGroup === 'contact') {
      const currentContact = newSeller.contact ?? { name: null, department: null, phone: null, fax: null, email: null, website: null };
      newSeller.contact = {
        ...currentContact,
        [field]: value || null,
      };
    } else if (fieldGroup === 'bankDetails') {
      const currentBankDetails = newSeller.bankDetails ?? { iban: '' };
      newSeller.bankDetails = {
            ...currentBankDetails,
        [field]: value || (field === 'iban' ? '' : undefined),
      };
    }

    setSeller(newSeller);
  };

  // Handler for numeric fields
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Convert to a number if it's a valid value, otherwise null
    const numberValue = value.trim() === '' ? null : Number(value);
    setSeller({ ...seller, [name]: numberValue });
  };

  // --- NEW: Logo Upload Logic ---
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    setIsUploading(true);
    const file = e.target.files?.[0];

    if (!file) {
      setIsUploading(false);
      return;
    }
    if (!currentUser) {
      setUploadError("Nicht eingeloggt.");
      setIsUploading(false);
        return;
      }

    // Validation
      const allowedTypes = ['image/png', 'image/jpeg', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
      setUploadError('Ungültiger Dateityp (Nur PNG, JPG, GIF).');
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = ""; // Reset input
      return;
    }
    if (file.size > 2 * 1024 * 1024) { // Max 2MB
      setUploadError('Datei zu groß (max. 2MB).');
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = ""; // Reset input
          return;
      }

    const storagePath = `logos/${currentUser.uid}/logo${file.name.substring(file.name.lastIndexOf('.'))}`; // Keep original extension
    const storageRef = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on('state_changed',
      (snapshot) => {
        // Optional: Handle progress updates
        // const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        // logger.debug(`upload is ${progress}% done`);
      },
      (error) => {
        logger.error(error, "storage upload error");
        setUploadError(`Fehler beim Hochladen: ${error.message}`);
        setIsUploading(false);
      },
      () => {
        // Upload completed successfully, now get the download URL
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          logger.info({url: downloadURL}, 'file available at storage');
          // Update the seller state via the passed setSeller function
          // This updates the localSellerData in the parent component (AccountPage/InvoiceDashboard)
          setSeller({ ...seller, logoUrl: downloadURL });
          setIsUploading(false);
          if (fileInputRef.current) fileInputRef.current.value = ""; // Reset input after successful upload
          setUploadError(null); // Clear any previous errors on successful upload
        }).catch((error) => {
             logger.error(error, "error getting download URL");
             setUploadError(`Fehler beim Abrufen der URL: ${error.message}`);
             setIsUploading(false);
        });
      }
    );
  };

  // --- UPDATED: Logo Removal Logic ---
  const handleRemoveLogo = async () => {
    if (!currentUser || !seller.logoUrl) {
      setUploadError("Logo oder Benutzer nicht gefunden.");
      return;
    }
    // Extract the path from the URL (might need adjustment based on URL format)
    // Basic extraction assuming default Firebase Storage URL structure
    const pathRegex = /logos%2F(.*)\?alt=media/;
    const match = seller.logoUrl.match(pathRegex);
    if (!match || !match[1]) {
        logger.error({logoUrl: seller.logoUrl}, "could not extract path from URL");
        setUploadError("Konnte Pfad aus Logo-URL nicht extrahieren.");
        return;
    }
    // Decode the path component
    const filePath = `logos/${decodeURIComponent(match[1])}`;
    logger.debug({filePath}, "attempting to delete logo file");

    const storageRef = ref(storage, filePath);
    setUploadError(null);
    setIsUploading(true); // Indicate activity

    try {
      // 1. Delete from Storage
      await deleteObject(storageRef);
      logger.info("logo deleted from storage");

      // 2. Update local state immediately via setSeller prop
      setSeller({ ...seller, logoUrl: null });
      if (fileInputRef.current) fileInputRef.current.value = "";
      setUploadError(null); // Clear any previous errors

      // 3. Call the prop function to handle Firestore update
      await onLogoRemoved(); // This will trigger saveSellerData({ logoUrl: null }) in parent
      logger.debug("firestore update triggered via onLogoRemoved");

    } catch (error: any) {
      logger.error(error, "error during logo removal process");
      setUploadError(`Fehler beim Löschen: ${error.message}`);
      // Consider specific error handling (e.g., if Storage fails, don't call onLogoRemoved?)
    } finally {
      setIsUploading(false);
    }
  };

// --- NEU: Small Business Checkbox Handler ---
  const handleSmallBusinessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSeller({ ...seller, isSmallBusiness: e.target.checked });
  };

  // --- M8: Versteuerungsart (Soll/Ist) — nicht protected, persistiert über den
  // bestehenden Speicher-Pfad des Formulars (saveSellerData), wie isSmallBusiness. ---
  const handleTaxationModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value === 'ist' ? 'ist' : 'soll';
    setSeller({ ...seller, taxationMode: value });
  };

  return (
    // Conditional container based on compact mode
    <div className={compact ? "" : "p-6 border border-hairline rounded-lg shadow-md bg-surface-raised mb-6"}>
      {!compact && <h2 className="text-2xl font-semibold mb-6 text-primary border-b border-hairline pb-3">{t('title')}</h2>}
      
      {/* Hinzugefügt: Erklärender Text für Pflichtfelder - nur wenn nicht compact */}
      {!compact && (
        <div className="bg-[rgb(var(--surface-base-rgb)/0.5)] p-4 rounded mb-6 border border-hairline">
          <p className="text-sm text-secondary mb-2">
            <span className="text-accent-ink font-medium">{t('descriptions.importantNote')}</span> {t('descriptions.requiredFieldsInfo')}
          </p>
          <p className="text-sm text-muted">
            {t('descriptions.afterSaveInfo')}
          </p>
        </div>
      )}
      
      {/* Use a main grid to structure the sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">

        {/* Section 1: Company Information */}
        <fieldset className={compact ? "space-y-4 p-4 rounded-md bg-[rgb(var(--surface-raised-rgb)/0.5)] border border-hairline" : "border border-hairline p-4 rounded-md bg-[rgb(var(--surface-base-rgb)/0.3)]"}>
          {!compact && <legend className="text-lg font-medium text-accent-ink px-2 mb-3">{t('sections.company')}</legend>}
          {compact && <h3 className="text-lg font-medium text-accent-ink mb-4">{t('sections.company')}</h3>}
          <div className="space-y-4">
            <div>
              <label htmlFor="sellerName" className="block text-sm font-medium text-secondary mb-1">
                {t('fields.name')} <span className="text-red-500">{t('labels.required')}</span>
              </label>
              <input 
                type="text" 
                id="sellerName" 
                name="name" 
                value={seller.name} 
                onChange={handleChange} 
                required 
                className={validationErrors.name ? 'input-field-error placeholder-extra-visible' : 'input-field placeholder-extra-visible'}
                placeholder={t('placeholders.name')} 
              />
              {validationErrors.name && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.name}</p>
              )}
            </div>
            <div>
              <label htmlFor="sellerStreet" className="block text-sm font-medium text-secondary mb-1">
                {t('fields.street')} <span className="text-red-500">{t('labels.required')}</span>
              </label>
              <input 
                type="text" 
                id="sellerStreet" 
                name="street" 
                value={seller.street} 
                onChange={handleChange} 
                required 
                className={validationErrors.street ? 'input-field-error placeholder-extra-visible' : 'input-field placeholder-extra-visible'}
                placeholder={t('placeholders.street')} 
              />
              {validationErrors.street && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.street}</p>
              )}
            </div>
            <div>
              <label htmlFor="sellerStreet2" className="block text-sm font-medium text-secondary mb-1">
                {t('fields.street2')} <span className="text-muted">{t('labels.optional')}</span>
              </label>
              <input 
                type="text" 
                id="sellerStreet2" 
                name="street2" 
                value={seller.street2 || ''} 
                onChange={handleChange} 
                className="input-field placeholder-visible" 
                placeholder={t('placeholders.street2')} 
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="sellerZip" className="block text-sm font-medium text-secondary mb-1">
                  {t('fields.zip')} <span className="text-red-500">{t('labels.required')}</span>
                </label>
                <input 
                  type="text" 
                  id="sellerZip" 
                  name="zip" 
                  value={seller.zip} 
                  onChange={handleChange} 
                  required 
                  className={validationErrors.zip ? 'input-field-error placeholder-extra-visible' : 'input-field placeholder-extra-visible'}
                  placeholder={t('placeholders.zip')} 
                />
                {validationErrors.zip && (
                  <p className="mt-1 text-sm text-red-500">{validationErrors.zip}</p>
                )}
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="sellerCity" className="block text-sm font-medium text-secondary mb-1">
                  {t('fields.city')} <span className="text-red-500">{t('labels.required')}</span>
                </label>
                <input 
                  type="text" 
                  id="sellerCity" 
                  name="city" 
                  value={seller.city} 
                  onChange={handleChange} 
                  required 
                  className={validationErrors.city ? 'input-field-error placeholder-extra-visible' : 'input-field placeholder-extra-visible'}
                  placeholder={t('placeholders.city')} 
                />
                {validationErrors.city && (
                  <p className="mt-1 text-sm text-red-500">{validationErrors.city}</p>
                )}
              </div>
            </div>
            <div>
              <label htmlFor="sellerCountry" className="block text-sm font-medium text-secondary mb-1">
                {t('fields.country')} <span className="text-red-500">{t('labels.required')}</span>
              </label>
            <input 
              type="text" 
              id="sellerCountry" 
              name="country" 
              value={seller.country} 
              onChange={handleChange} 
              required 
              maxLength={2}
              className={`uppercase ${validationErrors.country ? 'input-field-error placeholder-extra-visible' : 'input-field placeholder-extra-visible'}`}
              placeholder={t('placeholders.country')} 
            />
              {validationErrors.country && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.country}</p>
              )}
            </div>
             <div>
              <label htmlFor="sellerId" className="block text-sm font-medium text-secondary mb-1">
                {t('fields.sellerId')} <span className="text-muted">{t('labels.optional')}</span>
              </label>
              <input 
                type="text" 
                id="sellerId" 
                name="id" 
                value={seller.id || ''} 
                onChange={handleChange} 
                className="input-field placeholder-visible" 
                placeholder={t('placeholders.internalId')} 
              />
            </div>
          </div>
        </fieldset>

                {/* Section 2: Tax Information */}
        <fieldset className={compact ? "space-y-4 p-4 rounded-md bg-[rgb(var(--surface-raised-rgb)/0.5)] border border-hairline" : "border border-hairline p-4 rounded-md bg-[rgb(var(--surface-base-rgb)/0.3)]"}>
          <legend className="text-lg font-medium text-accent-ink px-2 mb-3">{t('sections.tax')}</legend>
             <div className="space-y-4">
              {/* Kleinunternehmer-Checkbox */}
              <div className="flex items-start mb-4 bg-surface-raised p-3 rounded border border-strong">
                <div className="flex items-center h-5">
                  <input
                    id="isSmallBusiness"
                    name="isSmallBusiness"
                    type="checkbox"
                    checked={seller.isSmallBusiness || false}
                    onChange={handleSmallBusinessChange}
                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="isSmallBusiness" className="font-medium text-primary">
                    {t('fields.isSmallBusiness')}
                  </label>
                  <p className="text-muted mt-1">{t('descriptions.smallBusinessInfo')}</p>
                </div>
              </div>

              {/* M8: Versteuerungsart (Soll/Ist) — bestimmt das Periodenkriterium der USt-Vorschau. */}
              <div className="mb-4">
                <label htmlFor="taxationMode" className="block text-sm font-medium text-secondary mb-1">
                  {t('mode.label', { ns: 'ustva' })}
                </label>
                <select
                  id="taxationMode"
                  name="taxationMode"
                  value={seller.taxationMode === 'ist' ? 'ist' : 'soll'}
                  onChange={handleTaxationModeChange}
                  className="input-field"
                >
                  <option value="soll">{t('mode.soll', { ns: 'ustva' })}</option>
                  <option value="ist">{t('mode.ist', { ns: 'ustva' })}</option>
                </select>
                <p className="text-muted mt-1 text-sm">{t('mode.info', { ns: 'ustva' })}</p>
              </div>

              <div>
                <label htmlFor="sellerVatNumber" className="block text-sm font-medium text-secondary mb-1">{t('fields.vatNumber')} <span className="text-muted">{t('labels.optional')}</span></label>
                <input type="text" id="sellerVatNumber" name="vatNumber" value={seller.vatNumber || ''} onChange={handleChange} className="input-field placeholder-visible" placeholder={t('placeholders.vatNumber')} />
              </div>
              <div>
                <label htmlFor="sellerTaxNumber" className="block text-sm font-medium text-secondary mb-1">{t('fields.taxNumber')} <span className="text-muted">{t('labels.optional')}</span></label>
                <input type="text" id="sellerTaxNumber" name="taxNumber" value={seller.taxNumber || ''} onChange={handleChange} className="input-field placeholder-visible" placeholder={t('placeholders.taxNumber')} />
              </div>
              <div>
                <label htmlFor="commercialRegisterNumber" className="block text-sm font-medium text-secondary mb-1">
                  {t('fields.commercialRegisterNumber')} <span className="text-muted">({t('labels.optional')})</span>
                </label>
                <input 
                  type="text" 
                  id="commercialRegisterNumber" 
                  name="commercialRegisterNumber" 
                  value={seller.commercialRegisterNumber || ''} 
                  onChange={handleChange} 
                  className="input-field placeholder-visible" 
                  placeholder={t('placeholders.commercialRegisterNumber')} 
                />
                <p className="text-xs text-[var(--tone-warning-fg)] mt-1">{t('descriptions.commercialRegisterInfo')}</p>
              </div>
              <div>
                <label htmlFor="commercialRegisterLocation" className="block text-sm font-medium text-secondary mb-1">
                  {t('fields.commercialRegisterLocation')} <span className="text-muted">({t('labels.optional')})</span>
                </label>
                <input 
                  type="text" 
                  id="commercialRegisterLocation" 
                  name="commercialRegisterLocation" 
                  value={seller.commercialRegisterLocation || ''} 
                  onChange={handleChange} 
                  className="input-field placeholder-visible" 
                  placeholder={t('placeholders.commercialRegisterLocation')} 
                />
                <p className="text-xs text-[var(--tone-warning-fg)] mt-1">{t('descriptions.commercialRegisterLocationInfo')}</p>
              </div>
              <div>
                 <label htmlFor="sellerGlobalId" className="block text-sm font-medium text-secondary mb-1">{t('fields.globalId')} <span className="text-muted">{t('labels.optional')}</span></label>
                 <input type="text" id="sellerGlobalId" name="globalId" value={seller.globalId || ''} onChange={handleChange} className="input-field placeholder-visible" placeholder={t('placeholders.globalId')} />
              </div>
              <div>
                 <label htmlFor="sellerGlobalIdScheme" className="block text-sm font-medium text-secondary mb-1">{t('fields.globalIdScheme')} <span className="text-muted">{t('labels.optional')}</span></label>
                 <input type="text" id="sellerGlobalIdScheme" name="globalIdScheme" value={seller.globalIdScheme || ''} onChange={handleChange} className="input-field placeholder-visible" placeholder={t('placeholders.globalIdScheme')} />
               </div>
            </div>
        </fieldset>

         {/* Section 3: Contact Person */}
        <fieldset className={compact ? "space-y-4 p-4 rounded-md bg-[rgb(var(--surface-raised-rgb)/0.5)] border border-hairline" : "border border-hairline p-4 rounded-md bg-[rgb(var(--surface-base-rgb)/0.3)]"}>
            <legend className="text-lg font-medium text-accent-ink px-2 mb-3">{t('sections.contact')} <span className="text-base font-normal text-muted">{t('labels.optional')}</span></legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contactName" className="block text-sm font-medium text-secondary mb-1">{t('fields.contactName')}</label>
                <input type="text" id="contactName" name="name" value={seller.contact?.name || ''} onChange={(e) => handleNestedChange(e, 'contact')} className="input-field placeholder-visible" placeholder={t('placeholders.contactName')} />
              </div>
              <div>
                <label htmlFor="contactDepartment" className="block text-sm font-medium text-secondary mb-1">{t('fields.department')}</label>
                <input type="text" id="contactDepartment" name="department" value={seller.contact?.department || ''} onChange={(e) => handleNestedChange(e, 'contact')} className="input-field placeholder-visible" placeholder={t('placeholders.department')} />
              </div>
               <div>
                <label htmlFor="contactEmail" className="block text-sm font-medium text-secondary mb-1">{t('fields.email')}</label>
                <input type="email" id="contactEmail" name="email" value={seller.contact?.email || ''} onChange={(e) => handleNestedChange(e, 'contact')} className="input-field placeholder-visible" placeholder={t('placeholders.email')} />
              </div>
               <div>
                <label htmlFor="contactPhone" className="block text-sm font-medium text-secondary mb-1">{t('fields.phone')}</label>
                <input type="tel" id="contactPhone" name="phone" value={seller.contact?.phone || ''} onChange={(e) => handleNestedChange(e, 'contact')} className="input-field placeholder-visible" placeholder={t('placeholders.phone')} />
              </div>
              <div>
                <label htmlFor="contactFax" className="block text-sm font-medium text-secondary mb-1">{t('fields.fax')}</label>
                <input type="tel" id="contactFax" name="fax" value={seller.contact?.fax || ''} onChange={(e) => handleNestedChange(e, 'contact')} className="input-field placeholder-visible" placeholder={t('placeholders.fax')} />
              </div>
              <div>
                <label htmlFor="contactWebsite" className="block text-sm font-medium text-secondary mb-1">{t('fields.website')}</label>
                <input type="text" id="contactWebsite" name="website" value={seller.contact?.website || ''} onChange={(e) => handleNestedChange(e, 'contact')} className="input-field placeholder-visible" placeholder={t('placeholders.website')} />
              </div>
            </div>
          </fieldset>
          
        {/* Section 4: Bank Details */}
        <fieldset className={compact ? "space-y-4 p-4 rounded-md bg-[rgb(var(--surface-raised-rgb)/0.5)] border border-hairline" : "border border-hairline p-4 rounded-md bg-[rgb(var(--surface-base-rgb)/0.3)]"}>
          <legend className="text-lg font-medium text-accent-ink px-2 mb-3">{t('sections.bank')} <span className="text-base font-normal text-muted">({t('descriptions.bankInfoXRechnung')})</span></legend>
          <div className="space-y-4">
              <div>
                <label htmlFor="bankAccountHolder" className="block text-sm font-medium text-secondary mb-1">
                  {t('fields.accountHolder')} <span className="text-muted">({t('descriptions.accountHolderInfo')})</span>
                </label>
                <input 
                  type="text" 
                  id="bankAccountHolder" 
                  name="accountHolder" 
                  value={seller.bankDetails?.accountHolder || ''} 
                  onChange={(e) => handleNestedChange(e, 'bankDetails')} 
                  className="input-field placeholder-visible" 
                  placeholder={t('placeholders.accountHolder')} 
                />
              </div>
              <div>
                <label htmlFor="bankIban" className="block text-sm font-medium text-secondary mb-1">{t('fields.iban')}</label>
                <input type="text" id="bankIban" name="iban" value={seller.bankDetails?.iban || ''} onChange={(e) => handleNestedChange(e, 'bankDetails')} className="input-field placeholder-visible" placeholder={t('placeholders.iban')} />
              </div>
              <div>
                <label htmlFor="bankBic" className="block text-sm font-medium text-secondary mb-1">{t('fields.bic')}</label>
                <input type="text" id="bankBic" name="bic" value={seller.bankDetails?.bic || ''} onChange={(e) => handleNestedChange(e, 'bankDetails')} className="input-field placeholder-visible uppercase" placeholder={t('placeholders.bic')} />
              </div>
              <div>
                <label htmlFor="bankName" className="block text-sm font-medium text-secondary mb-1">{t('fields.bankName')}</label>
                <input type="text" id="bankName" name="bankName" value={seller.bankDetails?.bankName || ''} onChange={(e) => handleNestedChange(e, 'bankDetails')} className="input-field placeholder-visible" placeholder={t('placeholders.bankName')} />
              </div>
          </div>
        </fieldset>

        {/* Section 5: Logo Upload - Spanning potentially 2 cols on lg? Or keep it within one column? Let's try one column first. */}
        <fieldset className={compact ? "space-y-4 p-4 rounded-md bg-[rgb(var(--surface-raised-rgb)/0.5)] border border-hairline" : "border border-hairline p-4 rounded-md bg-[rgb(var(--surface-base-rgb)/0.3)] lg:col-span-1"}>
            <legend className="text-lg font-medium text-accent-ink px-2 mb-3">{t('sections.logo')} <span className="text-base font-normal text-muted">{t('labels.optional')}</span></legend>
            <div className="flex flex-col items-center space-y-4">
                {seller.logoUrl ? (
                  <div className="mb-2 p-2 border border-strong rounded-md bg-white shadow-sm max-w-[200px]">
                     <img 
                       src={seller.logoUrl} 
                       alt={t('logo.current')} 
                       className="max-h-24 w-auto object-contain"
                       onError={(e) => {
                         logger.error(`Failed to load logo from URL: ${seller.logoUrl}`);
                         (e.target as HTMLImageElement).style.display = 'none';
                       }}
                       onLoad={() => {
                         // Only log logo load in development
                         if (process.env.NODE_ENV === 'development') {
                           logger.debug('Logo loaded successfully');
                         }
                       }}
                     />
                  </div>
                ) : (
                   <div className="h-24 w-full max-w-[200px] flex items-center justify-center border-2 border-dashed border-strong rounded-md bg-surface-inset text-muted text-sm">
                     {t('descriptions.noLogoUploaded')}
                   </div>
                )}
                 <div className="flex items-center space-x-4">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleLogoUpload}
                      accept="image/png, image/jpeg, image/gif"
                      className="hidden" // Hide the actual input
                      disabled={isUploading}
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()} // Trigger hidden input
                        disabled={isUploading}
                        className="px-4 py-2 text-sm rounded-md bg-indigo-600 hover:bg-indigo-700 text-[#0C1322] transition disabled:opacity-50"
                    >
                         {isUploading ? `${t('logo.uploading')}` : seller.logoUrl ? t('logo.change') : t('logo.upload')}
                    </button>
                    {seller.logoUrl && (
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        disabled={isUploading}
                        className="px-4 py-2 text-sm rounded-md bg-red-700 hover:bg-red-800 text-white transition disabled:opacity-50"
                      >
                        {isUploading ? '...' : t('logo.remove')}
                      </button>
                    )}
                </div>
                 {uploadError && <p className="text-red-500 text-xs mt-2">{uploadError}</p>}
                 <p className="text-xs text-muted">{t('logo.formats')}</p>
            </div>
        </fieldset>

        {/* Section 6: Invoice Numbering Configuration */} 
        <fieldset className={compact ? "space-y-4 p-4 rounded-md bg-[rgb(var(--surface-raised-rgb)/0.5)] border border-hairline" : "border border-hairline p-4 rounded-md bg-[rgb(var(--surface-base-rgb)/0.3)] lg:col-span-1"}>
          <legend className="text-lg font-medium text-accent-ink px-2 mb-3">{t('sections.invoicing')}</legend>
          <div className="space-y-4">
            <div>
               <label htmlFor="invoiceNumberFormat" className="block text-sm font-medium text-secondary mb-1">{t('descriptions.formatTemplateInfo')} <span className="text-muted">{t('labels.optional')}</span></label>
                <input
                  type="text"
                  id="invoiceNumberFormat"
                  name="invoiceNumberFormat"
                  value={seller.invoiceNumberFormat || ''}
                  onChange={handleChange}
                  className="input-field placeholder-visible font-mono"
                  placeholder={t('placeholders.invoiceNumberFormat')}
                />
                <p className="mt-1 text-xs text-muted">
                  {t('descriptions.placeholdersInfo')}
                </p>
            </div>
            <div>
              <label htmlFor="invoiceNumberStart" className="block text-sm font-medium text-secondary mb-1">{t('fields.invoiceNumberStart')} <span className="text-muted">{t('labels.optional')}</span></label>
                <input
                  type="number"
                  id="invoiceNumberStart"
                  name="invoiceNumberStart"
                  value={seller.invoiceNumberStart ?? ''} // Use empty string if null/undefined
                  onChange={handleNumberChange}
                  min="1"
                  step="1"
                  className="input-field placeholder-visible w-32"
                  placeholder={t('placeholders.invoiceNumberStart')}
                />
                <p className="text-xs text-muted mt-1">
                  {t('descriptions.nextNumberInfo')}
                </p>
            </div>
            {seller.invoiceNumberFormat && (
              <div className="mt-3">
                 <p className="block text-sm font-medium text-secondary mb-1">{t('preview.invoiceNumber')}:</p>
                 <p className="text-sm font-mono bg-surface-inset p-2 rounded-md text-primary">{previewInvoiceNumber || "Format eingeben..."}</p>
                 <p className="text-xs text-muted mt-1">{t('descriptions.currentNumberPreview')}: {seller.invoiceNumberCurrent ?? seller.invoiceNumberStart ?? 1}</p>
               </div>
            )}
          </div>
        </fieldset>

        {/* Section 7: Offer Numbering Configuration */} 
        <fieldset className={compact ? "space-y-4 p-4 rounded-md bg-[rgb(var(--surface-raised-rgb)/0.5)] border border-hairline" : "border border-hairline p-4 rounded-md bg-[rgb(var(--surface-base-rgb)/0.3)] lg:col-span-1"}>
          <legend className="text-lg font-medium text-accent-ink px-2 mb-3">{t('sections.offers')}</legend>
          <div className="space-y-4">
            <div>
               <label htmlFor="offerNumberFormat" className="block text-sm font-medium text-secondary mb-1">{t('descriptions.formatTemplateInfo')} <span className="text-muted">{t('labels.optional')}</span></label>
                <input
                  type="text"
                  id="offerNumberFormat"
                  name="offerNumberFormat"
                  value={seller.offerNumberFormat || ''}
                  onChange={handleChange}
                  className="input-field placeholder-visible font-mono"
                  placeholder={t('placeholders.offerNumberFormat')}
                />
                <p className="mt-1 text-xs text-muted">
                  {t('descriptions.placeholdersInfo')}
                </p>
            </div>
            <div>
              <label htmlFor="offerNumberStart" className="block text-sm font-medium text-secondary mb-1">{t('fields.offerNumberStart')} <span className="text-muted">{t('labels.optional')}</span></label>
                <input
                  type="number"
                  id="offerNumberStart"
                  name="offerNumberStart"
                  value={seller.offerNumberStart ?? ''} // Use empty string if null/undefined
                  onChange={handleNumberChange}
                  min="1"
                  step="1"
                  className="input-field placeholder-visible w-32"
                  placeholder={t('placeholders.offerNumberStart')}
                />
                <p className="text-xs text-muted mt-1">
                  {t('descriptions.nextNumberInfo')}
                </p>
            </div>
            {seller.offerNumberFormat && (
              <div className="mt-3">
                 <p className="block text-sm font-medium text-secondary mb-1">{t('preview.offerNumber')}:</p>
                 <p className="text-sm font-mono bg-surface-inset p-2 rounded-md text-primary">{previewOfferNumber || "Format eingeben..."}</p>
                 <p className="text-xs text-muted mt-1">{t('descriptions.currentNumberPreview')}: {seller.offerNumberCurrent ?? seller.offerNumberStart ?? 1}</p>
               </div>
            )}
          </div>
        </fieldset>

        {/* Section 8: Default Values for Invoices */}
        <fieldset className={compact ? "space-y-4 p-4 rounded-md bg-[rgb(var(--surface-raised-rgb)/0.5)] border border-hairline lg:col-span-2" : "border border-hairline p-4 rounded-md bg-[rgb(var(--surface-base-rgb)/0.3)] lg:col-span-2"}>
          <legend className="text-lg font-medium text-accent-ink px-2 mb-3">{t('sections.defaults')}</legend>
          <div className="space-y-4">
            <div>
              <label htmlFor="defaultPaymentTerms" className="block text-sm font-medium text-secondary mb-1">
                {t('fields.paymentTerms')} <span className="text-muted">{t('labels.optional')}</span>
              </label>
              <textarea
                id="defaultPaymentTerms"
                name="defaultPaymentTerms"
                value={seller.defaultPaymentTerms || ''}
                onChange={handleChange}
                className="input-field placeholder-visible"
                placeholder={t('placeholders.paymentTerms')}
                rows={2}
              />
              <p className="text-xs text-muted mt-1">
                {t('descriptions.paymentTermsInfo')}
              </p>
            </div>
            
            <div>
              <label htmlFor="defaultUnitCode" className="block text-sm font-medium text-secondary mb-1">
                {t('fields.defaultUnit')} <span className="text-muted">{t('labels.optional')}</span>
              </label>
              <UnitCodeSelector
                value={seller.defaultUnitCode || null}
                onChange={(code) => {
                  setSeller({
                    ...seller,
                    defaultUnitCode: code
                  });
                }}
                placeholder={t('placeholders.defaultUnit') || 'Standard-Einheit wählen...'}
              />
              <p className="text-xs text-muted mt-1">
                {t('descriptions.defaultUnitInfo')}
              </p>
            </div>
            
            <div>
              <label htmlFor="defaultNotes" className="block text-sm font-medium text-secondary mb-1">
                {t('fields.notes')} <span className="text-muted">{t('labels.optional')}</span>
              </label>
              <textarea
                id="defaultNotes"
                name="defaultNotes"
                value={seller.defaultNotes || ''}
                onChange={handleChange}
                className="textarea-field placeholder-visible"
                placeholder={t('placeholders.notes')}
                rows={3}
              />
              <p className="text-xs text-muted mt-1">
                {t('descriptions.notesInfo')}
              </p>
            </div>
          </div>
        </fieldset>

      </div> {/* End Main Grid */}
    </div> // End Main Container
  );
}; // End Component

export default SellerDetailsForm;

// Helper CSS class: die globale `.input-field`-Klasse in index.css nutzt die
// Design-v5-Token-Utilities (border-strong bg-surface-inset text-primary
// placeholder:text-muted, Fokus-Ring focus:ring-indigo-500 = Mint-Override).
